/**
 * Descripción editable del expediente (Sprint 11 · 5).
 *
 * Sin descripción: muestra link "+ Agregar descripción". Con descripción:
 * pinta el texto + botón editar. Form abre un textarea con max 10K chars
 * (espejo del backend).
 */

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { updateProject, type ProjectDetailDTO } from "@/lib/api";
import { useActiveMunicipalityDane } from "@/lib/municipality";

export function EditableDescription({
  project,
  onChanged,
}: {
  project: ProjectDetailDTO;
  onChanged: () => void;
}) {
  const activeDane = useActiveMunicipalityDane();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(project.description ?? "");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (value: string) => updateProject(project.id, activeDane, { description: value }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Error al guardar descripción.");
    },
  });

  const tooLong = draft.length > 10000;

  if (!editing) {
    if (!project.description) {
      return (
        <button
          type="button"
          onClick={() => {
            setDraft("");
            setEditing(true);
          }}
          className="mt-3 text-xs font-medium text-brand-700 hover:underline"
          data-testid="project-description-add"
        >
          + Agregar descripción
        </button>
      );
    }
    return (
      <div className="mt-3 rounded-md bg-slate-50 px-3 py-2">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
            Descripción
          </span>
          <button
            type="button"
            onClick={() => {
              setDraft(project.description ?? "");
              setEditing(true);
            }}
            className="text-xs font-medium text-brand-700 hover:underline"
            data-testid="project-description-edit"
          >
            editar
          </button>
        </div>
        <p
          className="text-sm leading-relaxed whitespace-pre-wrap text-slate-700"
          data-testid="project-description-value"
        >
          {project.description}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <label
        htmlFor="project-description-textarea"
        className="mb-1 block text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
      >
        Descripción del expediente
      </label>
      <textarea
        id="project-description-textarea"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={4}
        maxLength={10000}
        placeholder="Alcance, vereda, antecedentes, contexto que ayude al equipo a entender el proyecto."
        data-testid="project-description-textarea"
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => mutation.mutate(draft)}
          disabled={tooLong || mutation.isPending}
          data-testid="project-description-save"
          className="rounded-md bg-brand-700 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
        >
          {mutation.isPending ? "Guardando…" : "Guardar"}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setDraft(project.description ?? "");
            setError(null);
          }}
          className="text-xs font-medium text-slate-500 hover:text-slate-700"
        >
          Cancelar
        </button>
        <span
          className={
            "ml-auto text-[11px] " + (tooLong ? "font-semibold text-red-600" : "text-slate-500")
          }
        >
          {draft.length.toLocaleString("es-CO")}/10.000
        </span>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
